import { DateTime } from "luxon";
import type { CSSProperties } from "react";
import { NEUTRAL } from "../../../theme/tokens";
import { trackErrorEvent } from "../../../utils/analytics";
import { getStaffingDate, isAffectByDayStart, parseTimeString } from "../../../utils/dateUtils";
import {
	type ActualNurse,
	type DepartmentLocation,
	type GroupedDepartmentLocation,
	NurseFullRoleShort,
	type NurseInShift,
	NurseRole,
	type WeekData,
} from "../../../utils/models";
import { normalizeEnd } from "../../DayViewGaps/utils";

/**
 * This is an assumption, can be adjusted based on actual data
 */
const assumedAverageShiftEqualsOneStaffedEmployee = 12;

/**
 * each normalized block of shifts is 4 hours long
 */
const locationShiftBlockHoursDuration = 4;

// Belongs to the legacy (pre-APP-465) monthly view in this directory — unrelated to
// `MONTHLY_WEEKS_TO_SHOW` in `jobView/useMonthlyJobViewState.tsx`, which sizes the job-centric grid.
// The two happening to both be 6 is a coincidence; do not fold them together.
export const amountOfWeeksInMonthlyView = 6;
const startOfDayHourMonthlyView = 6; // 6 AM - fallback for emptyLocationConfig only

/**
 * Staffing severity for the day-amount tint.
 * Status colors (error/warning) are intentionally NOT brand-tied — severity
 * must stay readable regardless of the user's chosen theme color. DEFAULT
 * is the brand primary and DOES re-tint when the user picks a new color.
 */
export enum MonthlyViewDayAmountColors {
	CRITICAL = "#D32F2F", // theme.palette.error.main
	WARNING = "#ED6C02", // theme.palette.warning.main
	DEFAULT = "var(--inhouse-primary)",
}

export const defaultBorder = `1px solid ${NEUTRAL[300]}`;
// All re-tint with the user's chosen primary via CSS variables.
export const mainElementsColor = "var(--inhouse-primary)";
export const mainGridColor = "var(--inhouse-primary-dark)";
export const mainGridLocationColor = "var(--inhouse-primary)";
export const weekTimeFrameColor = "var(--inhouse-primary-alpha-08)";
export const weekTimeFrameHover = "var(--inhouse-primary-alpha-16)";
export const weekTimeFrameActive = "var(--inhouse-primary-alpha-08)";
export const monthlyViewHeaderMaxHeight = "60px";

/**
 * Shift-block tints. `warningColor` is status error at 20% alpha — must stay
 * stable across themes. `defaultColor` re-tints with the user's primary.
 */
export enum shiftColorArray {
	defaultColor = "var(--inhouse-primary-alpha-50)",
	warningColor = "#E71C0033",
}

export const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const satisfies readonly string[];
export const emptyLocationConfig = {
	location: "No Location",
	amountRequired: 0,
	roles: ["all"],
	days: weekDays.map((day) => day.toLowerCase()),
	shift_start: DateTime.now().startOf("day").set({ hour: startOfDayHourMonthlyView }).toFormat("HH:mm"),
	shift_end: DateTime.now().startOf("day").set({ hour: startOfDayHourMonthlyView }).plus({ days: 1 }).toFormat("HH:mm"),
	dayAmounts: {},
	amount: 0,
	location_short_name: "No Location",
	week_numbers: [1, 2],
	is_charge: false,
	section: "No Location Section",
	id: "no-location",
	department_id: "no-department",
	shift_type: "day",
} as const satisfies DepartmentLocation;
export const weekDaysStyle = {
	fontWeight: 400,
	fontStyle: "normal",
	fontSize: "14px",
	lineHeight: "36px",
	letterSpacing: "0%",
	textAlign: "center",
} as const satisfies CSSProperties;

export const weekDatesLeadingWidth = "150px";
export const weekDatesLeadingMinHeight = "120px";
export const expandedWeekLocationsMinHeight = "23px";
export const weekDatesLeadingMaxHeight = "160px";
export const actualShiftRenderGap = "4px";
export const roleColorfulIndicatorWidth = "8px";
export const shiftBreakdownItemMinWidth = "24px";
export const shiftBreakdownItemMinHeight = "23px";
export const weekDatesLeadingTypographyStyle = {
	fontWeight: 500,
	fontStyle: "normal",
	fontSize: "14px",
	lineHeight: "100%",
	letterSpacing: "0%",
	textAlign: "center",
} as const satisfies CSSProperties;

export const shiftBreakdownItemTypographyStyle = {
	fontWeight: 600,
	fontSize: "12px",
	lineHeight: "100%",
	letterSpacing: "0%",
	textAlign: "center",
	color: mainGridColor,
} as const satisfies CSSProperties;

export enum ShiftFilterType {
	All = "All",
	recurringShift = "Recurring Shifts",
	/**
	 * Overflow bucket for a caller that offers a CAPPED set of exact shift-time windows (the job-centric
	 * Monthly view): "every shift outside the windows on offer", so none goes uncounted. Opt-in via
	 * `TimeFilterChips`' `allowFilterOtherShifts`; `isSelectedTimeFrameValidForShiftFilter` matches
	 * nothing for it, because "outside the offered set" is only meaningful to the caller that defined
	 * that set — it resolves the bucket itself (`resolveShiftWindowKey`).
	 */
	Other = "Other",
}

export type ExactShiftFilterType = {
	start: DateTime;
	end: DateTime;
};

export type SlimShiftDuration = {
	startTime: DateTime;
	endTime: DateTime;
	shiftId?: string;
	amountOfMissingShifts?: number;
	amount?: number;
};

export const getRelevantShiftsForDate = ({
	specificWeekIndex,
	selectedShiftFilter,
	showActual,
	specificRoles,
	weekData,
	specificDayIndex,
	specificLocations,
	allowOverlap,
	dayStart,
}: {
	specificWeekIndex: number;
	specificDayIndex?: number;
	weekData: WeekData[];
	showActual: boolean;
	specificLocations?: DepartmentLocation[];
	specificRoles?: NurseRole[];
	selectedShiftFilter?: ShiftFilterType | ExactShiftFilterType | (ShiftFilterType | ExactShiftFilterType)[];
	allowOverlap: boolean;
	dayStart: string;
}): SlimShiftDuration[] | undefined => {
	return weekData[specificWeekIndex]?.weeklyShifts
		.flatMap((dayAndNightShifts) => (specificDayIndex != null ? dayAndNightShifts.days[specificDayIndex] : dayAndNightShifts.days))
		.map<ActualNurse[] | NurseInShift[] | undefined>((day) => (showActual ? day.nursesActual : day.nurses))
		.filter((value) => !!value)
		.flatMap<SlimShiftDuration>((actualOrNurseInShift: ActualNurse[] | NurseInShift[]) =>
			(specificLocations
				? actualOrNurseInShift.filter((curr: ActualNurse | NurseInShift) => {
						return specificLocations.some(
							(specificLocation) =>
								(specificLocation.location === curr.location && specificLocation.roles.some((r) => r === "all" || r === curr.role)) ||
								((!curr.location || curr.location.length === 0) &&
									specificLocations.some((loc) => loc.location === emptyLocationConfig.location || !loc.location)),
							/*If we have an empty location string for this shift, map it to the grouped empty location config.*/
						);
					})
				: actualOrNurseInShift
			)
				.filter(
					(curr: ActualNurse | NurseInShift) =>
						(specificRoles === undefined || specificRoles.includes(NurseRole[NurseFullRoleShort[curr.role]])) &&
						isSelectedTimeFrameValidForShiftFilter({
							startTime: curr.startTime,
							endTime: curr.endTime,
							selectedShiftFilter,
							isRecurringShift: !showActual ? !!(curr as NurseInShift)?.recurringShiftId : undefined,
							allowOverlap,
							dayStart,
						}),
				)
				.flatMap<SlimShiftDuration>((curr: ActualNurse | NurseInShift) => ({
					startTime: curr.startTime,
					endTime: curr.endTime,
					shiftId: curr.id,
				})),
		);
};

/**
 * Normalizes relevant shifts per location for a given date.
 * We go over the earliest and latest shift times(or location times) with 4 hour blocks,
 * and for each block fill the amount with shifts that are relevant + missing shifts amount.
 * @param relevantShifts
 * @param groupedLocation
 * @param currentDate - the date for which we are normalizing the shifts(day of the shifts)
 */
export const normalizeRelevantShiftsPerLocation = ({
	relevantShifts,
	groupedLocation,
	currentDate,
}: {
	groupedLocation: GroupedDepartmentLocation | undefined | null;
	relevantShifts: SlimShiftDuration[] | undefined;
	currentDate: DateTime;
}): SlimShiftDuration[] => {
	const returnedNormalizedShifts: SlimShiftDuration[] = [];
	const relevantShiftsSortedByTime = relevantShifts?.sort((a, b) => a.startTime.diff(b.startTime).milliseconds || a.endTime.diff(b.endTime).milliseconds) ?? [];
	const earliestShiftStartTime = relevantShiftsSortedByTime.length > 0 ? relevantShiftsSortedByTime[0].startTime : null;
	const latestShiftEndTime = relevantShiftsSortedByTime.length > 0 ? relevantShiftsSortedByTime[relevantShiftsSortedByTime.length - 1].endTime : null;
	const locationStartTime = groupedLocation
		? currentDate.set({ hour: groupedLocation.earliestLocationStartTime.hour, minute: groupedLocation.earliestLocationStartTime.minute })
		: null;
	const locationEnd = groupedLocation
		? currentDate.set({ hour: groupedLocation.latestLocationEndTime.hour, minute: groupedLocation.latestLocationEndTime.minute })
		: null;
	const locationEndTime = locationEnd && locationStartTime ? (locationEnd.hour <= locationStartTime.hour ? locationEnd.plus({ day: 1 }) : locationEnd) : null;

	const startTimes = [earliestShiftStartTime, locationStartTime].filter(Boolean) as DateTime[];
	const earliestTime = startTimes.length > 0 ? DateTime.min(...startTimes) : undefined;
	const endTimed = [latestShiftEndTime, locationEndTime].filter(Boolean) as DateTime[];
	const latestTime = endTimed.length > 0 ? DateTime.max(...endTimed) : undefined;

	if (!earliestTime || !latestTime) {
		// If we don't have a valid time range, return an empty array
		if ((relevantShiftsSortedByTime && relevantShiftsSortedByTime.length > 0) || groupedLocation) {
			trackErrorEvent("normalizeRelevantShiftsPerLocation_error_invalid_time_range", {
				relevantShiftsSortedByTime,
				groupedLocation,
				currentDate: currentDate.toISO(),
				earliestTime,
				latestTime,
			});
		}
		return [];
	}

	for (let currentTime = earliestTime; currentTime < latestTime; currentTime = currentTime.plus({ hours: locationShiftBlockHoursDuration })) {
		const currentEndTime = currentTime.plus({ hours: locationShiftBlockHoursDuration });
		const currentRequiredEmployees = groupedLocation
			? getAmountRequiredForGroupedLocationTimeFrame(groupedLocation, currentTime, currentEndTime, currentDate)
			: 0;

		const equivalentShiftCount = relevantShiftsSortedByTime.reduce((acc, shift) => {
			const overlapStart = shift.startTime > currentTime ? shift.startTime : currentTime;
			const overlapEnd = shift.endTime < currentEndTime ? shift.endTime : currentEndTime;

			// We count the overlap over the current block of time
			const overlapDuration = overlapEnd.diff(overlapStart, "hours").hours;
			const overlapHours = Math.max(0, overlapDuration);
			return acc + Math.min(1, overlapHours / locationShiftBlockHoursDuration);
		}, 0);
		const amountOfShiftsInBlock = Math.round(equivalentShiftCount);
		returnedNormalizedShifts.push({
			startTime: currentTime,
			endTime: currentEndTime,
			amountOfMissingShifts: currentRequiredEmployees > amountOfShiftsInBlock ? currentRequiredEmployees - amountOfShiftsInBlock : 0,
			amount: amountOfShiftsInBlock,
		});
	}

	// dont return empty normalized shifts(must have either amount or amountOfMissingShifts)
	return returnedNormalizedShifts.filter((normalizedShift) => normalizedShift.amount || normalizedShift.amountOfMissingShifts);
};

export const getActualEmployeeHoursForDate = ({
	specificWeekIndex,
	selectedShiftFilter,
	specificRoles,
	showActual,
	weekData,
	specificDayIndex,
	dayStart,
}: {
	specificWeekIndex: number;
	specificDayIndex: number;
	weekData: WeekData[];
	showActual: boolean;
	specificRoles?: NurseRole[];
	selectedShiftFilter?: ShiftFilterType | ExactShiftFilterType | (ShiftFilterType | ExactShiftFilterType)[];
	dayStart: string;
}) => {
	const relevantShifts = getRelevantShiftsForDate({
		specificWeekIndex,
		specificDayIndex,
		weekData,
		showActual,
		specificRoles,
		selectedShiftFilter,
		allowOverlap: true,
		dayStart,
	});
	return (
		relevantShifts?.reduce((totalHours, shiftTimes) => {
			const shiftStart = shiftTimes.startTime;
			let shiftEnd = shiftTimes.endTime;
			if (shiftEnd < shiftStart) {
				shiftEnd = shiftEnd.plus({ days: 1 });
			}
			return totalHours + shiftEnd.diff(shiftStart, "hours").hours;
		}, 0) ?? 0
	);
};

export const isSelectedTimeFrameValidForShiftFilter = ({
	startTime,
	endTime,
	isRecurringShift,
	selectedShiftFilter,
	allowOverlap = false,
	dayStart,
}: {
	startTime: DateTime;
	endTime?: DateTime;
	selectedShiftFilter?: ShiftFilterType | ExactShiftFilterType | (ShiftFilterType | ExactShiftFilterType)[];
	isRecurringShift?: boolean;
	allowOverlap?: boolean;
	dayStart: string;
}) => {
	const staffingDate = getStaffingDate(startTime, dayStart);
	const { hour: dayStartHour } = parseTimeString(dayStart);
	const startOfDayShift = staffingDate.startOf("day").set({ hour: dayStartHour });

	/**
	 * edge case of start time that comes before start of day - should add 1 day to it because that is technically considered an overnight shift
	 */
	const actualizedStartTime = isAffectByDayStart(staffingDate, startTime.hour, startTime.minute, dayStart);
	const actualizedEndTime = endTime ? (endTime <= actualizedStartTime ? endTime.plus({ day: 1 }) : endTime) : null;

	const overlapsWithinSlack = (shiftStart: DateTime, shiftEnd: DateTime | null, winStart: DateTime, winEnd: DateTime, slackHours = 1) => {
		const end = shiftEnd?.startOf("hour") ?? winEnd; // open-ended shift: treat as covering up to window end
		const overlapStart = DateTime.max(shiftStart.startOf("hour"), winStart);
		const overlapEnd = DateTime.min(end, winEnd);
		const overlapMs = Math.max(0, overlapEnd.diff(overlapStart, "millisecond").milliseconds);
		const windowMs = winEnd.diff(winStart, "millisecond").milliseconds;
		const slackMs = slackHours * 60 * 60 * 1000;
		// require overlap to be at least window - 1h (i.e., at most 1h total outside)
		return overlapMs >= Math.max(0, windowMs - slackMs);
	};

	const checkFilterAgainstTimes = (filterToTest: ShiftFilterType | ExactShiftFilterType) => {
		const inWindowStrict = (wStart: DateTime, wEnd: DateTime) =>
			actualizedStartTime.startOf("hour") >= wStart &&
			actualizedStartTime.startOf("hour") < wEnd &&
			(!actualizedEndTime || (actualizedEndTime.startOf("hour") > wStart && actualizedEndTime.startOf("hour") <= wEnd));

		const inWindowWith1hSlack = (wStart: DateTime, wEnd: DateTime) => overlapsWithinSlack(actualizedStartTime, actualizedEndTime, wStart, wEnd, 1);

		if (filterToTest === ShiftFilterType.All) return true;
		if (typeof filterToTest !== "object") return false;

		const startToTest = isAffectByDayStart(staffingDate, filterToTest.start.hour, 0, dayStart);
		const endToTest = startOfDayShift.set({ hour: filterToTest.end.hour });
		const endToTestNormalized = normalizeEnd(startToTest, endToTest);

		const inSlack = inWindowWith1hSlack(startToTest, endToTestNormalized);
		const inStrict = inWindowStrict(startToTest, endToTestNormalized);
		return allowOverlap ? inStrict || inSlack : inStrict;
	};

	const isArray = Array.isArray(selectedShiftFilter);
	const isThereRecurringShiftFilter = isArray
		? selectedShiftFilter.includes(ShiftFilterType.recurringShift)
		: selectedShiftFilter === ShiftFilterType.recurringShift;
	return (
		!selectedShiftFilter ||
		((!isThereRecurringShiftFilter || (isThereRecurringShiftFilter && (isRecurringShift === undefined || isRecurringShift))) &&
			(isArray ? selectedShiftFilter.some((filter) => checkFilterAgainstTimes(filter)) : checkFilterAgainstTimes(selectedShiftFilter)))
	);
};

export const getRelevantLocationConfigsForDate = (
	date: DateTime,
	departmentLocationsConfig: DepartmentLocation[],
	dayStart: string,
	specificRoles?: NurseRole[],
	selectedShiftFilter?: ShiftFilterType | ExactShiftFilterType | (ShiftFilterType | ExactShiftFilterType)[],
) => {
	return departmentLocationsConfig.filter((value) => {
		const locationStart = DateTime.fromFormat(value.shift_start, "HH:mm");
		return (
			!!value.location &&
			value.days.map((day) => day.toLowerCase()).includes(date.toFormat("ccc").toLowerCase()) &&
			(specificRoles === undefined ||
				(value.roles.some((val) => val === "all" || specificRoles?.includes(NurseRole[NurseFullRoleShort[val]])) &&
					isSelectedTimeFrameValidForShiftFilter({
						startTime: locationStart,
						selectedShiftFilter,
						dayStart,
					})))
		);
	});
};

export const getRequiredEmployeesTotalHoursForDate = ({
	date,
	specificRoles,
	selectedShiftFilter,
	departmentLocationsConfig,
	dayStart,
}: {
	date: DateTime;
	departmentLocationsConfig: DepartmentLocation[];
	specificRoles?: NurseRole[];
	selectedShiftFilter?: ShiftFilterType | ExactShiftFilterType | (ShiftFilterType | ExactShiftFilterType)[];
	dayStart: string;
}) => {
	const relevantLocationConfigs = getRelevantLocationConfigsForDate(date, departmentLocationsConfig, dayStart, specificRoles, selectedShiftFilter);
	return relevantLocationConfigs
		.map((config) => {
			let timeEnd = DateTime.fromFormat(config.shift_end, "HH:mm");
			const timeStart = DateTime.fromFormat(config.shift_start, "HH:mm");
			if (timeEnd < timeStart) {
				timeEnd = timeEnd.plus({ day: 1 });
			}
			const dayAbbr = date.setLocale("en").toFormat("ccc").toLowerCase();
			const dayRequired = config.dayAmounts?.[dayAbbr]?.amount_required ?? config.amountRequired;
			return timeEnd.diff(timeStart, "hours").hours * dayRequired;
		})
		.reduce((acc, hours) => acc + hours, 0);
};

export type GroupedShift = {
	startTime: DateTime;
	endTime: DateTime;
	amount: number;
	amountOfMissingShifts: number;
	shifts: SlimShiftDuration[];
	/**
	 * Should be float number
	 */
	shiftDurationInHours: number;
};
export type MonthlyViewBreakdownLanes = GroupedShift[][];

/**
 * Groups shifts into lanes based on their start and end times.
 * Each lane contains groups of shifts that do not overlap.
 * different lanes means that the lanes overlap in time.
 * If shifts have the same start and end time, they are grouped together.
 * @param relevantShifts - Array of shifts to be grouped.
 * @param selectedShiftFilter - Optional filter to apply to the shifts.
 */
export function groupShiftsIntoLanes({
	relevantShifts,
	selectedShiftFilter,
	dayStart,
}: {
	relevantShifts: SlimShiftDuration[];
	selectedShiftFilter?: ShiftFilterType | ExactShiftFilterType | (ShiftFilterType | ExactShiftFilterType)[];
	dayStart: string;
}): MonthlyViewBreakdownLanes {
	let sortedShifts = relevantShifts.sort((a, b) => {
		// Sort by startTime then endTime
		return a.startTime.diff(b.startTime).milliseconds || a.endTime.diff(b.endTime).milliseconds;
	});

	if (selectedShiftFilter) {
		// Filter shifts based on the selected shift filter
		sortedShifts = sortedShifts.filter((value) =>
			isSelectedTimeFrameValidForShiftFilter({
				startTime: value.startTime,
				endTime: value.endTime,
				selectedShiftFilter,
				dayStart,
			}),
		);
	}

	const lanes: MonthlyViewBreakdownLanes = [];

	const doOverlap = (a: SlimShiftDuration | GroupedShift, b: SlimShiftDuration | GroupedShift) => {
		const aStart = a.startTime;
		const aEnd = a.endTime < aStart ? a.endTime.plus({ days: 1 }) : a.endTime;
		const bStart = b.startTime;
		const bEnd = b.endTime < bStart ? b.endTime.plus({ days: 1 }) : b.endTime;
		return aStart < bEnd && bStart < aEnd;
	};

	for (const shift of sortedShifts) {
		let placed = false;

		// Try placing into existing lanes
		for (const lane of lanes) {
			let inserted = false;
			for (const group of lane) {
				const sameTime = shift.startTime.hasSame(group.startTime, "millisecond") && shift.endTime.hasSame(group.endTime, "millisecond");
				if (sameTime) {
					group.shifts.push(shift);
					if (shift.amountOfMissingShifts != null) {
						group.amountOfMissingShifts += shift.amountOfMissingShifts;
					}
					if (shift.amount != null) {
						group.amount += shift.amount;
					}
					inserted = true;
					placed = true;
					break;
				}
			}
			if (inserted) break;

			const hasOverlap = lane.some((group) => doOverlap(group, shift));
			if (!hasOverlap) {
				// No overlap in lane → add new group
				lane.push({
					startTime: shift.startTime,
					endTime: shift.endTime,
					amount: shift.amount ?? 0,
					shifts: [shift],
					shiftDurationInHours: shift.endTime.diff(shift.startTime, "hours").as("hours"),
					amountOfMissingShifts: shift.amountOfMissingShifts ?? 0,
				});
				placed = true;
				break;
			}
		}

		if (!placed) {
			// Create a new lane
			lanes.push([
				{
					startTime: shift.startTime,
					endTime: shift.endTime,
					amount: shift.amount ?? 0,
					shifts: [shift],
					shiftDurationInHours: shift.endTime.diff(shift.startTime, "hours").as("hours"),
					amountOfMissingShifts: shift.amountOfMissingShifts ?? 0,
				},
			]);
		}
	}

	return lanes;
}

/**
 * Merges adjacent lanes in the MonthlyViewBreakdownLanes.
 * This function checks if the last shift in the current merged lane has the same amount and is adjacent to the next shift.
 * The merging is limited by the assumed average shift duration, which is set to 12 hours.
 * @param lanes - The MonthlyViewBreakdownLanes to merge, usually the result of groupShiftsIntoLanes.
 */
export function mergeAdjacentLanes(lanes: MonthlyViewBreakdownLanes): MonthlyViewBreakdownLanes {
	const result: MonthlyViewBreakdownLanes = [];
	for (const currentLane of lanes) {
		if (currentLane.length === 1) {
			result.push(currentLane);
		} else {
			const currentMergedLane: GroupedShift[] = [];
			for (const groupedShift of currentLane) {
				if (currentMergedLane.length === 0) {
					currentMergedLane.push(groupedShift);
				} else {
					const lastShift = currentMergedLane[currentMergedLane.length - 1];
					if (
						lastShift.amount === groupedShift.amount &&
						lastShift.amountOfMissingShifts === groupedShift.amountOfMissingShifts &&
						groupedShift.startTime >= lastShift.startTime &&
						groupedShift.endTime >= lastShift.endTime &&
						lastShift.shiftDurationInHours + groupedShift.shiftDurationInHours <= assumedAverageShiftEqualsOneStaffedEmployee
					) {
						// Merge with the last shift in the current merged lane
						lastShift.shifts.push(...groupedShift.shifts);
						lastShift.endTime = groupedShift.endTime;
						lastShift.shiftDurationInHours += groupedShift.shiftDurationInHours;
					} else {
						// No merge, push as a new shift
						currentMergedLane.push(groupedShift);
					}
				}
			}
			result.push(currentMergedLane);
		}
	}
	return result;
}

/**
 * Returns the total amount of required employees for a grouped location within a specific time frame.
 * e.g if we have multiple DepartmentLocations with the same name and same hours - for a given time frame we will return the total amount of required employees for all of them.
 */
const getAmountRequiredForGroupedLocationTimeFrame = (
	groupedLocation: GroupedDepartmentLocation,
	startTime: DateTime,
	endTime: DateTime,
	currentDate: DateTime,
): number => {
	const blockHours = Math.max(0, endTime.diff(startTime, "hour").as("hours"));
	if (blockHours === 0) return 0;
	let amountRequired = 0;
	for (const currentLocation of groupedLocation.locations) {
		let locationStart: DateTime = DateTime.fromFormat(currentLocation.shift_start, "HH:mm");
		let locationEnd: DateTime = DateTime.fromFormat(currentLocation.shift_end, "HH:mm");
		locationStart = currentDate.set({ hour: locationStart.hour, minute: locationStart.minute, second: 0, millisecond: 0 });
		locationEnd = currentDate.set({ hour: locationEnd.hour, minute: locationEnd.minute, second: 0, millisecond: 0 });
		if (locationEnd < locationStart) {
			locationEnd = locationEnd.plus({ day: 1 });
		}

		// Overlap between [startTime, endTime] and [locationStart, locationEnd]
		const overlapStart = startTime > locationStart ? startTime : locationStart;
		const overlapEnd = endTime < locationEnd ? endTime : locationEnd;
		const overlapHours = Math.max(0, overlapEnd.diff(overlapStart, "hour").as("hours"));

		if (overlapHours > 0) {
			// Fraction of the block that this config covers
			const fractionOfBlock = overlapHours / blockHours; // [0..1]
			amountRequired += Math.round(currentLocation.amountRequired * fractionOfBlock);
		}
	}

	return amountRequired;
};

export const getEarliestAndLatestLocationTimes = (locations: DepartmentLocation[]): { earliestStart: DateTime; latestEnd: DateTime } | null => {
	if (!locations || locations.length === 0) {
		return null;
	}
	let earliestStart = DateTime.fromFormat(locations[0].shift_start, "HH:mm");
	let latestEnd = DateTime.fromFormat(locations[0].shift_end, "HH:mm");
	latestEnd = latestEnd <= earliestStart ? latestEnd.plus({ day: 1 }) : latestEnd;

	for (const location of locations) {
		const start = DateTime.fromFormat(location.shift_start, "HH:mm");
		let end = DateTime.fromFormat(location.shift_end, "HH:mm");
		end = end <= start ? end.plus({ days: 1 }) : end;

		if (start < earliestStart) {
			earliestStart = start;
		}
		if (end > latestEnd) {
			latestEnd = end;
		}
	}

	return { earliestStart, latestEnd };
};

/**
 * Calculates the number of missing employees based on expected and actual hours.
 * For missing amount - we get a negative number, for excess - a positive number.
 */
export const getMissingEmployeesCalculation = (expectedHours: number, actualHours: number) => {
	return Math.round((actualHours - expectedHours) / assumedAverageShiftEqualsOneStaffedEmployee);
};

/**
 * Calculates the assumed number of employees based on hoursAmount.
 * @param hoursAmount
 */
export const getAssumedEmployeesCalculation = (hoursAmount: number) => {
	return Math.round(hoursAmount / assumedAverageShiftEqualsOneStaffedEmployee);
};

export const missingEmployeesToColor = (missingEmployees: number): MonthlyViewDayAmountColors => {
	if (missingEmployees >= -1) {
		return MonthlyViewDayAmountColors.DEFAULT;
	}
	if (missingEmployees > -5) {
		return MonthlyViewDayAmountColors.WARNING;
	}
	return MonthlyViewDayAmountColors.CRITICAL;
};
